import {
  MSG,
  PROTOCOL_VERSION,
  parseError,
  parseMatchEnd,
  parseMatchInit,
  parsePong,
  parseSnapshot,
  type JoinOptions,
  type SnapshotPayload,
} from '@orbomb/protocol';
import {
  cloneState,
  step,
  type Camp,
  type InputVector,
  type MatchState,
  type GoldenBallState,
  type PlayerBallState,
  type TickInputs,
} from '@orbomb/sim';
import type { LocalSession } from '../game/session';
import { simInputForCamp } from './inputMap';
import type { ConnectMatch, MatchSocket, ReconnectMatch } from './colyseus';
import { unrankedNote, type OnlineRoom } from './parse';
import { clearResumeSeat, saveResumeSeat } from './resume';

const STILL: InputVector = { x: 0, y: 0 };
export const MAX_INPUT_HISTORY = 48;
export const MAX_SNAP_FRAMES = 48;
export const MAX_PENDING_SNAPSHOTS = 8;
const SEND_EVERY_MS = 16;
const PING_EVERY_MS = 500;
/** Respawn-sized jumps snap. Knockback and catch-up must still smear. */
const TELEPORT_CELLS = 6;
/** How far ahead of the last snapshot the local sim may run (RTT + jitter). */
export const MAX_PREDICT_TICKS = 18;
export const PLAYBACK_RATE_MIN = 0.88;
export const PLAYBACK_RATE_MAX = 1.12;
/** Visual correction larger than this snaps; smaller errors ease out. */
const ERROR_SNAP_CELLS = 2;
const ERROR_DECAY_MS = 80;
/** A one-frame jump bigger than max-speed travel is a hitch, not physics. */
const DISCONTINUITY_CELLS = 0.25;
export const SIM_TICK_MS = 1000 / 60;
/**
 * Draw remotes this far behind the latest snapshot so jitter can play out.
 * Local prediction is not delayed. Architecture §22–27: ~100 ms.
 */
export const INTERP_DELAY_TICKS = 6;
export const INTERP_DELAY_MS = INTERP_DELAY_TICKS * SIM_TICK_MS;
/** Diagnostic only. Production (mode C) holds the last pose instead of flying through walls. */
export const MAX_EXTRAPOLATE_TICKS = 0;

/**
 * A — snapshot poses overwrite the draw (old hitch path).
 * B — local prediction, ignore visual corrections.
 * C — predict local; interpolate remotes and goldens 100 ms behind (production).
 */
export type NetViewMode = 'A' | 'B' | 'C';
export const NET_VIEW_MODES: readonly NetViewMode[] = ['A', 'B', 'C'];

export function readNetViewMode(): NetViewMode {
  if (typeof window === 'undefined') return 'C';
  const raw = new URLSearchParams(window.location.search).get('net');
  return raw === 'A' || raw === 'B' || raw === 'C' ? raw : 'C';
}

export function cycleNetViewMode(mode: NetViewMode): NetViewMode {
  const i = NET_VIEW_MODES.indexOf(mode);
  return NET_VIEW_MODES[(i + 1) % NET_VIEW_MODES.length]!;
}

export function formatNetStats(stats: NetStats): string {
  const ms = (value: number | null) => (value === null ? '—' : value.toFixed(0));
  return [
    `net ${stats.mode}  rtt ${ms(stats.rttMs)}ms  jitter ${stats.jitterMs.toFixed(0)}ms`,
    `snap ${ms(stats.snapshotIntervalMs)}ms  age ${ms(stats.snapshotAgeMs)}ms  buf ${stats.bufferedTicks.toFixed(1)}`,
    `err ${stats.predictError.toFixed(2)}c  jumps ${stats.discontinuities}  tick ${stats.serverTick}/${stats.renderTick.toFixed(1)}`,
    `q ${stats.pendingSnapshots} hist ${stats.historyLength} frames ${stats.bufferedSnapshots} pred ${stats.predictSteps} replay ${stats.replaySteps} snap ${stats.snapshotMs.toFixed(1)}ms`,
  ].join('\n');
}

export interface NetStats {
  mode: NetViewMode;
  rttMs: number | null;
  jitterMs: number;
  snapshotIntervalMs: number | null;
  snapshotAgeMs: number | null;
  predictError: number;
  discontinuities: number;
  serverTick: number;
  renderTick: number;
  bufferedTicks: number;
  pendingSnapshots: number;
  historyLength: number;
  bufferedSnapshots: number;
  predictSteps: number;
  replaySteps: number;
  snapshotMs: number;
}

export interface LiveProbe {
  pendingSnapshots: number;
  historyLength: number;
  bufferedSnapshots: number;
  predictSteps: number;
  replaySteps: number;
  snapshotMs: number;
  clones: number;
  renderTick: number;
  appliedTick: number;
  discontinuities: number;
}

/** Blend 0 is the previous snapshot, 1 is the latest. Waiting holds the last pose. */
export function onlineDrawBlend(
  accMs: number,
  spanMs: number,
  waiting: boolean,
  paused: boolean,
): number {
  if (waiting || paused) return 1;
  if (!(spanMs > 0)) return 1;
  return Math.min(1, Math.max(0, accMs / spanMs));
}

export interface RemotePose {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export interface SnapFrame {
  tick: number;
  receivedAt: number;
  players: Record<string, RemotePose>;
  goldens: Record<string, RemotePose>;
}

export function lerpRemoteInto(
  dest: RemotePose,
  from: RemotePose,
  to: RemotePose,
  t: number,
): RemotePose {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx * dx + dy * dy > TELEPORT_CELLS * TELEPORT_CELLS) {
    const src = t < 1 ? from : to;
    dest.x = src.x;
    dest.y = src.y;
    dest.vx = src.vx;
    dest.vy = src.vy;
    return dest;
  }
  const u = Math.min(1, Math.max(0, t));
  dest.x = from.x + dx * u;
  dest.y = from.y + dy * u;
  dest.vx = from.vx + (to.vx - from.vx) * u;
  dest.vy = from.vy + (to.vy - from.vy) * u;
  return dest;
}

export function lerpRemote(from: RemotePose, to: RemotePose, t: number): RemotePose {
  return lerpRemoteInto({ x: 0, y: 0, vx: 0, vy: 0 }, from, to, t);
}

export function shiftPose(pose: RemotePose, ticks: number): RemotePose {
  const dt = ticks / 60;
  return {
    x: pose.x + pose.vx * dt,
    y: pose.y + pose.vy * dt,
    vx: pose.vx,
    vy: pose.vy,
  };
}

/**
 * Advance the interpolation playhead. The delay is a *set point* (stay ~100 ms
 * behind the newest snapshot), not a ceiling that freezes motion between 20 Hz
 * arrivals. Never pass `latest` — that would extrapolate through walls.
 */
export function advancePlaybackTick(
  renderTick: number,
  oldest: number,
  latest: number,
  dtTicks: number,
  delayTicks = INTERP_DELAY_TICKS,
  _maxExtrapolate = MAX_EXTRAPOLATE_TICKS,
): number {
  if (latest < oldest) latest = oldest;
  if (renderTick < oldest) renderTick = oldest;
  if (renderTick > latest) renderTick = latest;
  const step = Math.max(0, dtTicks);
  if (step === 0) return renderTick;
  // Do not ride the newest snapshot while the delay buffer is filling.
  // That catches `latest` and freezes remotes/goldens until the next 20 Hz packet.
  if (latest - oldest < delayTicks) return oldest;
  const desired = Math.max(oldest, latest - delayTicks);
  if (step > delayTicks + 6) return desired;
  const error = desired - renderTick;
  let rate = 1;
  if (error > 2) rate = 1.08;
  if (error > 8) rate = PLAYBACK_RATE_MAX;
  if (error < -2) rate = 0.94;
  if (error < -8) rate = PLAYBACK_RATE_MIN;
  return Math.min(latest, Math.max(oldest, renderTick + step * rate));
}

export type SampledPoses = {
  players: Record<string, RemotePose>;
  goldens: Record<string, RemotePose>;
};

export function sampleAtTick(
  frames: readonly SnapFrame[],
  tick: number,
  dest?: SampledPoses,
): SampledPoses | null {
  const out = dest ?? { players: {}, goldens: {} };
  return sampleAtTickInto(frames, tick, out);
}

export function sampleAtTickInto(
  frames: readonly SnapFrame[],
  tick: number,
  dest: SampledPoses,
): SampledPoses | null {
  if (frames.length === 0) return null;
  if (tick <= frames[0]!.tick) {
    copyPosesInto(frames[0]!.players, dest.players);
    copyPosesInto(frames[0]!.goldens, dest.goldens);
    return dest;
  }
  const last = frames[frames.length - 1]!;
  if (tick >= last.tick) {
    copyPosesInto(last.players, dest.players);
    copyPosesInto(last.goldens, dest.goldens);
    return dest;
  }
  let i = 0;
  while (i + 1 < frames.length && frames[i + 1]!.tick <= tick) i += 1;
  const a = frames[i]!;
  const b = frames[i + 1];
  if (!b || tick <= a.tick) {
    copyPosesInto(a.players, dest.players);
    copyPosesInto(a.goldens, dest.goldens);
    return dest;
  }
  const span = b.tick - a.tick;
  const u = span > 0 ? (tick - a.tick) / span : 1;
  mixPosesInto(a.players, b.players, u, dest.players);
  mixPosesInto(a.goldens, b.goldens, u, dest.goldens);
  return dest;
}

function poseSlot(bag: Record<string, RemotePose>, id: string): RemotePose {
  const existing = bag[id];
  if (existing) return existing;
  const created: RemotePose = { x: 0, y: 0, vx: 0, vy: 0 };
  bag[id] = created;
  return created;
}

function copyPosesInto(src: Record<string, RemotePose>, dest: Record<string, RemotePose>): void {
  for (const id of Object.keys(dest)) {
    if (!(id in src)) delete dest[id];
  }
  for (const id of Object.keys(src)) {
    const from = src[id]!;
    const slot = poseSlot(dest, id);
    slot.x = from.x;
    slot.y = from.y;
    slot.vx = from.vx;
    slot.vy = from.vy;
  }
}

function mixPosesInto(
  a: Record<string, RemotePose>,
  b: Record<string, RemotePose>,
  t: number,
  dest: Record<string, RemotePose>,
): void {
  for (const id of Object.keys(dest)) {
    if (!(id in a) && !(id in b)) delete dest[id];
  }
  for (const id of Object.keys(a)) {
    const from = a[id]!;
    const to = b[id];
    if (!to) copyPose(poseSlot(dest, id), from);
    else lerpRemoteInto(poseSlot(dest, id), from, to, t);
  }
  for (const id of Object.keys(b)) {
    if (id in a) continue;
    copyPose(poseSlot(dest, id), b[id]!);
  }
}

function copyPose(dest: RemotePose, src: RemotePose): void {
  dest.x = src.x;
  dest.y = src.y;
  dest.vx = src.vx;
  dest.vy = src.vy;
}

function captureFrame(state: MatchState, receivedAt: number): SnapFrame {
  const players: Record<string, RemotePose> = {};
  for (const player of state.players) {
    players[player.id] = { x: player.x, y: player.y, vx: player.vx, vy: player.vy };
  }
  const goldens: Record<string, RemotePose> = {};
  for (const golden of state.goldens) {
    goldens[golden.id] = { x: golden.x, y: golden.y, vx: golden.vx, vy: golden.vy };
  }
  return { tick: state.tick, receivedAt, players, goldens };
}

function localId(camp: Camp): PlayerBallState['id'] {
  return camp === 'ORANGE' ? 'player-orange' : 'player-turquoise';
}

function findPlayer(state: MatchState, camp: Camp): PlayerBallState | undefined {
  return state.players.find((player) => player.camp === camp);
}

export type InputSample = { tick: number; sequence: number; x: number; y: number };

/** One sample per simulation tick; a later stick on the same tick overwrites. */
export function recordInputSample(
  history: InputSample[],
  sample: InputSample,
  max = MAX_INPUT_HISTORY,
): void {
  const last = history[history.length - 1];
  if (last && last.tick === sample.tick) {
    history[history.length - 1] = sample;
  } else if (!last || last.tick < sample.tick) {
    history.push(sample);
  }
  if (history.length > max) history.splice(0, history.length - max);
}

/** Stick held at `tick`: exact sample, else the latest earlier sample (server hold-last). */
export function sampleForTick(
  history: readonly InputSample[],
  tick: number,
): InputSample | undefined {
  for (let i = history.length - 1; i >= 0; i -= 1) {
    if (history[i]!.tick <= tick) return history[i];
  }
  return undefined;
}

export function pruneHistory(history: InputSample[], ackTick: number): void {
  while (history.length > 0 && history[0]!.tick < ackTick) history.shift();
}

/**
 * Authoritative Colyseus snapshots plus local prediction.
 * The local marble steps immediately; remotes interpolate (and extrapolate
 * briefly if a snapshot is late) so hosted RTT does not freeze the world.
 */
export class LiveMatch {
  private socket: MatchSocket | undefined;
  private sequence = 0;
  private lastInput: InputVector = STILL;
  private started = false;
  private left = false;
  private closing = false;
  private reported = false;
  private fault: string | null = null;
  private connecting = true;
  private appliedTick = -1;
  private renderTick = 0;
  private readonly frames: SnapFrame[] = [];
  private readonly history: InputSample[] = [];
  private pending: SnapshotPayload[] = [];
  private flushScheduled = false;
  private predicted: MatchState | null = null;
  private heldRemote: InputVector = STILL;
  private clockOriginMs: number | null = null;
  private clockOriginTick = 0;
  private predictElapsedMs = 0;
  private errorX = 0;
  private errorY = 0;
  private mode: NetViewMode = readNetViewMode();
  private lastSentAt = 0;
  private lastPingAt = 0;
  private lastSnapshotAt = 0;
  private readonly rtts: number[] = [];
  private readonly snapshotGaps: number[] = [];
  private discontinuities = 0;
  private lastView: Record<string, RemotePose> = {};
  private lastPredictSteps = 0;
  private lastReplaySteps = 0;
  private lastSnapshotMs = 0;
  private clones = 0;
  private viewScratch: MatchState | null = null;
  private viewPlayers: [PlayerBallState, PlayerBallState] | null = null;
  private viewGoldens: GoldenBallState[] = [];
  private readonly sampled: SampledPoses = { players: {}, goldens: {} };
  private readonly onDrop: () => void;
  private hardResync = false;
  /** Wait for MATCH_END after the result SNAPSHOT before leaving the socket. */
  private matchEndWait: ReturnType<typeof setTimeout> | null = null;
  readonly camp: Camp;
  /** Settles when the Colyseus socket is bound or the join attempt fails. */
  readonly ready: Promise<void>;

  constructor(
    private readonly session: LocalSession,
    private readonly room: OnlineRoom,
    camp: Camp,
    private readonly connect: ConnectMatch,
    private readonly onSync: (apply: () => void) => void,
    private readonly onNote: () => void,
    onOpponentLeft: () => void,
    private readonly accessToken?: () => Promise<string | null>,
    private readonly resumeToken?: string | null,
    private readonly reconnect?: ReconnectMatch,
    /** The seat could not be taken (no match server, join refused). The UI leaves the match. */
    private readonly onJoinFailed?: (message: string) => void,
  ) {
    this.camp = camp;
    this.session.beginOnline(room.seed, camp);
    this.onDrop = onOpponentLeft;
    this.ready = this.open();
  }

  waiting(): boolean {
    return !this.session.state.result && !this.started;
  }

  blendSpanMs(): number {
    return INTERP_DELAY_MS;
  }

  drawBlend(_now: number, _paused: boolean): number {
    return 1;
  }

  predictedTick(): number {
    if (this.predicted) return this.predicted.tick;
    return this.appliedTick < 0 ? this.session.state.tick : this.appliedTick;
  }

  setMode(mode: NetViewMode): void {
    this.mode = mode;
  }

  getMode(): NetViewMode {
    return this.mode;
  }

  cycleMode(): NetViewMode {
    this.mode = cycleNetViewMode(this.mode);
    return this.mode;
  }

  netStats(now = performance.now()): NetStats {
    const latest = this.frames[this.frames.length - 1];
    const rtt = this.rtts.length ? this.rtts[this.rtts.length - 1]! : null;
    const mean = this.rtts.length
      ? this.rtts.reduce((sum, value) => sum + value, 0) / this.rtts.length
      : 0;
    const jitter = this.rtts.length
      ? Math.sqrt(
          this.rtts.reduce((sum, value) => sum + (value - mean) * (value - mean), 0) /
            this.rtts.length,
        )
      : 0;
    const gap = this.snapshotGaps.length ? this.snapshotGaps[this.snapshotGaps.length - 1]! : null;
    const local = findPlayer(this.predicted ?? this.session.state, this.camp);
    const server = findPlayer(this.session.state, this.camp);
    const predictError =
      local && server
        ? Math.hypot(local.x + this.errorX - server.x, local.y + this.errorY - server.y)
        : 0;
    return {
      mode: this.mode,
      rttMs: rtt,
      jitterMs: jitter,
      snapshotIntervalMs: gap,
      snapshotAgeMs: this.lastSnapshotAt ? now - this.lastSnapshotAt : null,
      predictError,
      discontinuities: this.discontinuities,
      serverTick: this.appliedTick,
      renderTick: this.renderTick,
      bufferedTicks: latest ? latest.tick - this.renderTick : 0,
      pendingSnapshots: this.pending.length,
      historyLength: this.history.length,
      bufferedSnapshots: this.frames.length,
      predictSteps: this.lastPredictSteps,
      replaySteps: this.lastReplaySteps,
      snapshotMs: this.lastSnapshotMs,
    };
  }

  probe(): LiveProbe {
    return {
      pendingSnapshots: this.pending.length,
      historyLength: this.history.length,
      bufferedSnapshots: this.frames.length,
      predictSteps: this.lastPredictSteps,
      replaySteps: this.lastReplaySteps,
      snapshotMs: this.lastSnapshotMs,
      clones: this.clones,
      renderTick: this.renderTick,
      appliedTick: this.appliedTick,
      discontinuities: this.discontinuities,
    };
  }

  /** Advance playback + prediction and return the interpolated world. */
  present(now: number, dtMs: number): MatchState {
    if (this.left) return this.session.state;
    this.advanceClock(dtMs);
    if (this.mode !== 'B') this.decayError(dtMs);
    this.predictToward(now, dtMs);
    const view = this.viewState();
    this.noteDiscontinuities(view, dtMs);
    return view;
  }

  viewState(): MatchState {
    const src = this.session.state;
    const [a, b] = src.players;
    if (!a || !b) return src;
    const latest = this.frames[this.frames.length - 1];
    const sampled =
      this.mode === 'C'
        ? sampleAtTickInto(this.frames, this.renderTick, this.sampled)
        : latest
          ? sampleAtTickInto(this.frames, latest.tick, this.sampled)
          : null;
    const predicted = this.predicted;
    const mine = localId(this.camp);
    const localPredicted = predicted ? findPlayer(predicted, this.camp) : undefined;
    const correctLocal = this.mode === 'C';
    const usePredictedLocal = this.mode !== 'A' && Boolean(localPredicted);
    const players = this.paintPlayers(
      a,
      b,
      mine,
      sampled,
      localPredicted,
      usePredictedLocal,
      correctLocal,
    );
    const goldens = this.paintGoldens(src.goldens, sampled);
    const view = this.viewScratch;
    if (!view) {
      this.viewScratch = { ...src, players, goldens };
      return this.viewScratch;
    }
    view.simulationVersion = src.simulationVersion;
    view.contentHash = src.contentHash;
    view.balanceHash = src.balanceHash;
    view.mapId = src.mapId;
    view.seed = src.seed;
    view.tick = this.mode === 'C' ? Math.floor(this.renderTick) : src.tick;
    view.rng = src.rng;
    view.players = players;
    view.goldens = goldens;
    view.totems = src.totems;
    view.explosions = src.explosions;
    view.nextExplosionId = src.nextExplosionId;
    view.bonuses = src.bonuses;
    view.nextBonusId = src.nextBonusId;
    view.clock = src.clock;
    view.spawnRegion = src.spawnRegion;
    view.stats = src.stats;
    view.result = src.result;
    return view;
  }

  statusLine(): string {
    if (this.session.state.result) return `Online · ${this.camp}`;
    if (this.fault) return `Online · ${this.fault}`;
    if (this.connecting) return 'Online · connecting to the match server';
    if (this.waiting()) return 'Online · get ready';
    const diag = this.mode === 'C' ? '' : ` · diag ${this.mode}`;
    const stale =
      this.lastSnapshotAt && performance.now() - this.lastSnapshotAt > 800
        ? ' · waiting for server'
        : '';
    return `Online · you are ${this.camp} at the bottom${diag}${stale}`;
  }

  queue(screen: InputVector): void {
    if (this.left || this.session.state.result) return;
    this.lastInput = simInputForCamp(screen, this.camp);
  }

  flush(now: number): void {
    if (this.left || this.session.state.result || !this.socket) return;
    if (this.lastSentAt !== 0 && now - this.lastSentAt < SEND_EVERY_MS) {
      this.maybePing(now);
      return;
    }
    this.lastSentAt = now;
    this.sequence += 1;
    this.socket.send(MSG.INPUT, {
      sequence: this.sequence,
      tick: this.predictedTick(),
      x: this.lastInput.x,
      y: this.lastInput.y,
    });
    this.maybePing(now);
  }

  dispose(): void {
    this.left = true;
    this.disarmPageHideResume();
    this.clearMatchEndWait();
    if (this.closing || this.session.state.result) clearResumeSeat();
    this.releaseMatchSocket();
    this.pending.length = 0;
    this.history.length = 0;
    this.frames.length = 0;
    this.predicted = null;
    this.viewScratch = null;
    this.viewPlayers = null;
    this.viewGoldens = [];
    this.sampled.players = {};
    this.sampled.goldens = {};
    this.lastView = {};
    this.flushScheduled = false;
  }

  private releaseMatchSocket(): void {
    const socket = this.socket;
    if (!socket) return;
    this.closing = true;
    socket.disableReconnection?.();
    socket.leave(true);
    this.socket = undefined;
  }

  private consentedLeave(socket: MatchSocket): void {
    this.closing = true;
    socket.disableReconnection?.();
    socket.leave(true);
  }

  private advanceClock(dtMs: number): void {
    if (this.frames.length === 0) return;
    const oldest = this.frames[0]!.tick;
    const latest = this.frames[this.frames.length - 1]!.tick;
    this.renderTick = advancePlaybackTick(
      this.renderTick,
      oldest,
      latest,
      Math.max(0, dtMs) / SIM_TICK_MS,
    );
  }

  private decayError(dtMs: number): void {
    if (this.errorX === 0 && this.errorY === 0) return;
    const decay = Math.exp(-Math.max(0, dtMs) / ERROR_DECAY_MS);
    this.errorX *= decay;
    this.errorY *= decay;
    if (this.errorX * this.errorX + this.errorY * this.errorY < 1e-6) {
      this.errorX = 0;
      this.errorY = 0;
    }
  }

  private predictToward(now: number, dtMs = 0): void {
    this.lastPredictSteps = 0;
    if (!this.started || this.appliedTick < 0 || this.session.state.result) return;
    if (this.clockOriginMs === null) {
      this.clockOriginMs = now;
      this.clockOriginTick = this.appliedTick;
      this.predictElapsedMs = 0;
    }
    this.predictElapsedMs += Math.max(0, dtMs);
    if (!this.predicted) {
      this.predicted = cloneState(this.session.state);
      this.clones += 1;
    }
    const predicted = this.predicted;
    if (predicted.tick < this.appliedTick) {
      this.predicted = cloneState(this.session.state);
      this.clones += 1;
    }
    const synced = this.predicted;
    if (!synced) return;
    const fromNow = this.clockOriginTick + Math.floor((now - this.clockOriginMs) / SIM_TICK_MS);
    const fromDt = this.clockOriginTick + Math.floor(this.predictElapsedMs / SIM_TICK_MS);
    const wallTick = Math.max(fromNow, fromDt);
    const want = Math.min(wallTick, this.appliedTick + MAX_PREDICT_TICKS);
    while (synced.tick < want && this.lastPredictSteps < MAX_PREDICT_TICKS) {
      const sample = sampleForTick(this.history, synced.tick);
      const local = sample ? { x: sample.x, y: sample.y } : this.lastInput;
      const inputs: TickInputs = {
        ORANGE: this.camp === 'ORANGE' ? local : this.heldRemote,
        TURQUOISE: this.camp === 'TURQUOISE' ? local : this.heldRemote,
      };
      const processTick = synced.tick;
      step(synced, this.session.config, inputs);
      if (synced.tick <= processTick) break;
      recordInputSample(this.history, {
        tick: processTick,
        sequence: this.sequence,
        x: inputs[this.camp].x,
        y: inputs[this.camp].y,
      });
      this.lastPredictSteps += 1;
    }
  }

  private async open(): Promise<void> {
    try {
      let socket: MatchSocket;
      if (this.resumeToken && this.reconnect) {
        socket = await this.reconnect(this.resumeToken);
        this.hardResync = true;
      } else {
        const token = (await this.accessToken?.()) ?? null;
        if (!token) {
          this.connecting = false;
          this.fault = 'Sign in again to join the match server.';
          this.onNote();
          if (!this.left) this.onJoinFailed?.(this.fault);
          return;
        }
        const options: JoinOptions = {
          supabaseRoomId: this.room.id,
          seed: this.room.seed,
          mapId: this.room.mapId,
          camp: this.camp,
          accessToken: token,
        };
        socket = await this.connect(options);
      }
      if (this.left) {
        this.consentedLeave(socket);
        return;
      }
      this.bindSocket(socket);
      this.rememberResume(socket);
      this.connecting = false;
      this.onNote();
    } catch (error) {
      this.connecting = false;
      // Keep the resume token on a blip; loadResumeSeat expires it after the hold.
      this.fault =
        error instanceof Error && error.message
          ? error.message
          : 'Could not reach the match server.';
      this.onNote();
      // Nothing will ever arrive on this seat: do not sit on GET READY.
      if (!this.left) this.onJoinFailed?.(this.fault);
    }
  }

  private bindSocket(socket: MatchSocket): void {
    this.socket = socket;
    this.armPageHideResume();
    socket.onMessage(MSG.MATCH_INIT, (payload) => this.onInit(payload));
    socket.onMessage(MSG.SNAPSHOT, (payload) => this.onSnapshot(payload));
    socket.onMessage(MSG.PONG, (payload) => this.onPong(payload));
    socket.onMessage(MSG.MATCH_END, (payload) => void this.onEnd(payload));
    socket.onMessage(MSG.ERROR, (payload) => this.onFault(payload));
    socket.onDrop?.(() => {
      if (this.left || this.closing || this.session.state.result) return;
      this.rememberResume(socket);
      this.fault = 'Reconnecting to the match server';
      this.onNote();
    });
    socket.onReconnect?.(() => {
      if (this.left || this.closing) return;
      this.hardResync = true;
      this.frames.length = 0;
      this.predicted = null;
      this.appliedTick = -1;
      this.renderTick = 0;
      this.errorX = 0;
      this.errorY = 0;
      this.fault = null;
      this.rememberResume(socket);
      this.onNote();
    });
    socket.onLeave?.(() => {
      if (this.left || this.closing || this.session.state.result) return;
      clearResumeSeat();
      this.fault = 'Lost the match server.';
      this.onNote();
    });
  }

  /** Freshen the token timestamp on tab close so a new tab can resume within the hold. */
  private readonly onPageHide = (): void => {
    if (this.left || this.closing || this.session.state.result) return;
    if (this.socket) this.rememberResume(this.socket);
  };

  private armPageHideResume(): void {
    if (typeof window === 'undefined') return;
    window.removeEventListener('pagehide', this.onPageHide);
    window.addEventListener('pagehide', this.onPageHide);
  }

  private disarmPageHideResume(): void {
    if (typeof window === 'undefined') return;
    window.removeEventListener('pagehide', this.onPageHide);
  }

  private rememberResume(socket: MatchSocket): void {
    const token = socket.reconnectionToken;
    if (!token || this.session.state.result) return;
    saveResumeSeat({
      token,
      supabaseRoomId: this.room.id,
      camp: this.camp,
    });
  }

  private onInit(payload: unknown): void {
    if (this.left) return;
    const init = parseMatchInit(payload);
    if (!init) return;
    if (init.protocolVersion !== PROTOCOL_VERSION) {
      this.fault = 'This client is the wrong protocol version.';
      this.onNote();
      return;
    }
    if (init.simulationVersion !== this.session.config.simulationVersion) {
      this.fault = 'This client is the wrong simulation version.';
      this.onNote();
      return;
    }
    if (init.contentHash !== this.session.config.contentHash) {
      this.fault = 'This client has the wrong content pack.';
      this.onNote();
      return;
    }
    if (init.camp !== this.camp) {
      this.session.onlineCamp = init.camp;
    }
    if (init.players >= 2) this.started = true;
    this.onNote();
  }

  private onSnapshot(payload: unknown): void {
    if (this.left) return;
    const snap = parseSnapshot(payload);
    if (!snap) return;
    // Forfeit ends on the current tick (no step). Still accept that SNAPSHOT.
    if (snap.tick < this.appliedTick) return;
    if (snap.tick === this.appliedTick && !snap.state.result) return;
    this.pending.push(snap);
    if (this.pending.length > MAX_PENDING_SNAPSHOTS) {
      this.pending.splice(0, this.pending.length - MAX_PENDING_SNAPSHOTS);
    }
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    queueMicrotask(() => this.flushQueued());
  }

  private flushQueued(): void {
    this.flushScheduled = false;
    if (this.left) {
      this.pending.length = 0;
      return;
    }
    const batch = this.pending
      .splice(0, this.pending.length)
      .filter(
        (snap) =>
          snap.tick > this.appliedTick ||
          (snap.tick === this.appliedTick && Boolean(snap.state.result)),
      )
      .sort((a, b) => a.tick - b.tick);
    if (batch.length === 0) return;
    const events = batch.flatMap((snap) => [...snap.events]);
    const last = batch[batch.length - 1]!;
    this.started = true;
    this.session.waitingForPeer = false;
    const drawn = this.drawnLocal();
    const startedAt = performance.now();
    try {
      this.onSync(() => {
        const receivedAt = performance.now();
        if (this.lastSnapshotAt) {
          this.snapshotGaps.push(receivedAt - this.lastSnapshotAt);
          if (this.snapshotGaps.length > 40) this.snapshotGaps.shift();
        }
        this.lastSnapshotAt = receivedAt;
        this.session.applyServerState(last.state, events);
        for (const snap of batch) {
          this.frames.push(captureFrame(snap.state, receivedAt));
          this.appliedTick = snap.tick;
        }
        if (this.frames.length > MAX_SNAP_FRAMES) {
          this.frames.splice(0, this.frames.length - MAX_SNAP_FRAMES);
        }
        const remoteCamp: Camp = this.camp === 'ORANGE' ? 'TURQUOISE' : 'ORANGE';
        this.heldRemote = last.inputs[remoteCamp];
        this.clockOriginMs = null;
        this.clockOriginTick = last.tick;
        this.predictElapsedMs = 0;
        if (this.hardResync) {
          this.hardResync = false;
          this.predicted = null;
          this.errorX = 0;
          this.errorY = 0;
          this.renderTick = last.tick;
          pruneHistory(this.history, last.ackTick ?? last.tick);
        } else {
          this.reconcile(last, drawn);
          pruneHistory(this.history, last.ackTick ?? last.tick);
          if (this.frames.length === batch.length) this.renderTick = this.frames[0]!.tick;
        }
      });
    } catch {
      this.fault = 'The match state could not be applied.';
      this.onNote();
      return;
    }
    this.lastSnapshotMs = performance.now() - startedAt;
    this.onNote();
    if (last.state.result) {
      // Keep the socket open for MATCH_END (ladder persist). Do not leave yet.
      this.noteMatchOver('pending');
      this.armMatchEndWait();
    }
  }

  private armMatchEndWait(): void {
    if (this.matchEndWait !== null || this.reported) return;
    this.matchEndWait = setTimeout(() => {
      this.matchEndWait = null;
      if (this.left || this.reported) return;
      this.releaseMatchSocket();
      this.noteMatchOver('failed');
    }, 8_000);
  }

  private clearMatchEndWait(): void {
    if (this.matchEndWait === null) return;
    clearTimeout(this.matchEndWait);
    this.matchEndWait = null;
  }

  private maybePing(now: number): void {
    if (!this.socket || now - this.lastPingAt < PING_EVERY_MS) return;
    this.lastPingAt = now;
    this.socket.send(MSG.PING, { t: now });
  }

  private onPong(payload: unknown): void {
    if (this.left) return;
    const pong = parsePong(payload);
    if (!pong) return;
    const rtt = performance.now() - pong.t;
    if (!Number.isFinite(rtt) || rtt < 0 || rtt > 5000) return;
    this.rtts.push(rtt);
    if (this.rtts.length > 40) this.rtts.shift();
  }

  private noteDiscontinuities(view: MatchState, dtMs: number): void {
    const dt = Math.max(dtMs, 1) / 1000;
    const maxTravel = 6.25 * dt + DISCONTINUITY_CELLS;
    const check = (id: string, x: number, y: number) => {
      const prev = this.lastView[id];
      this.lastView[id] = { x, y, vx: 0, vy: 0 };
      if (!prev) return;
      const dist = Math.hypot(x - prev.x, y - prev.y);
      if (dist > maxTravel) this.discontinuities += 1;
    };
    for (const player of view.players) check(player.id, player.x, player.y);
    for (const golden of view.goldens) check(golden.id, golden.x, golden.y);
  }

  private drawnLocal(): RemotePose | null {
    const player = findPlayer(this.predicted ?? this.session.state, this.camp);
    if (!player) return null;
    return {
      x: player.x + this.errorX,
      y: player.y + this.errorY,
      vx: player.vx,
      vy: player.vy,
    };
  }

  private reconcile(last: SnapshotPayload, drawn: RemotePose | null): void {
    this.lastReplaySteps = 0;
    this.predicted = cloneState(last.state);
    this.clones += 1;
    const ackTick = last.ackTick ?? last.tick;
    const newest = this.history[this.history.length - 1]?.tick ?? ackTick - 1;
    const until = Math.min(newest + 1, ackTick + MAX_PREDICT_TICKS);
    const predicted = this.predicted;
    while (predicted.tick < until && this.lastReplaySteps < MAX_PREDICT_TICKS) {
      const sample = sampleForTick(this.history, predicted.tick);
      const local = sample ? { x: sample.x, y: sample.y } : this.lastInput;
      const inputs: TickInputs = {
        ORANGE: this.camp === 'ORANGE' ? local : this.heldRemote,
        TURQUOISE: this.camp === 'TURQUOISE' ? local : this.heldRemote,
      };
      const processTick = predicted.tick;
      step(predicted, this.session.config, inputs);
      if (predicted.tick <= processTick) break;
      this.lastReplaySteps += 1;
    }
    const next = findPlayer(predicted, this.camp);
    if (!drawn || !next) {
      this.errorX = 0;
      this.errorY = 0;
      return;
    }
    const dx = drawn.x - next.x;
    const dy = drawn.y - next.y;
    if (dx * dx + dy * dy > ERROR_SNAP_CELLS * ERROR_SNAP_CELLS) {
      this.errorX = 0;
      this.errorY = 0;
      return;
    }
    this.errorX = dx;
    this.errorY = dy;
  }

  private paintPlayers(
    a: PlayerBallState,
    b: PlayerBallState,
    mine: PlayerBallState['id'],
    sampled: { players: Record<string, RemotePose> } | null,
    localPredicted: PlayerBallState | undefined,
    usePredictedLocal: boolean | undefined,
    correctLocal: boolean,
  ): [PlayerBallState, PlayerBallState] {
    if (!this.viewPlayers) this.viewPlayers = [{ ...a }, { ...b }];
    this.paintPlayer(
      this.viewPlayers[0],
      a,
      mine,
      sampled,
      localPredicted,
      usePredictedLocal,
      correctLocal,
    );
    this.paintPlayer(
      this.viewPlayers[1],
      b,
      mine,
      sampled,
      localPredicted,
      usePredictedLocal,
      correctLocal,
    );
    return this.viewPlayers;
  }

  private paintPlayer(
    dest: PlayerBallState,
    src: PlayerBallState,
    mine: PlayerBallState['id'],
    sampled: { players: Record<string, RemotePose> } | null,
    localPredicted: PlayerBallState | undefined,
    usePredictedLocal: boolean | undefined,
    correctLocal: boolean,
  ): void {
    dest.id = src.id;
    dest.camp = src.camp;
    dest.status = src.status;
    dest.stunUntilTick = src.stunUntilTick;
    dest.protectedUntilTick = src.protectedUntilTick;
    if (src.id === mine && usePredictedLocal && localPredicted) {
      dest.x = localPredicted.x + (correctLocal ? this.errorX : 0);
      dest.y = localPredicted.y + (correctLocal ? this.errorY : 0);
      dest.vx = localPredicted.vx;
      dest.vy = localPredicted.vy;
      return;
    }
    const pose = sampled?.players[src.id];
    dest.x = pose ? pose.x : src.x;
    dest.y = pose ? pose.y : src.y;
    dest.vx = pose ? pose.vx : src.vx;
    dest.vy = pose ? pose.vy : src.vy;
  }

  private paintGoldens(
    src: readonly GoldenBallState[],
    sampled: { goldens: Record<string, RemotePose> } | null,
  ): GoldenBallState[] {
    const dest = this.viewGoldens;
    while (dest.length < src.length) dest.push({ ...src[dest.length]! });
    dest.length = src.length;
    for (let i = 0; i < src.length; i += 1) {
      const golden = src[i]!;
      const slot = dest[i]!;
      slot.id = golden.id;
      slot.status = golden.status;
      slot.armedAtTick = golden.armedAtTick;
      slot.detonationTick = golden.detonationTick;
      slot.firstToucher = golden.firstToucher;
      slot.lastToucher = golden.lastToucher;
      slot.armedBy = golden.armedBy;
      slot.chainedByExplosionId = golden.chainedByExplosionId;
      slot.respawnAtTick = golden.respawnAtTick;
      slot.activeAtTick = golden.activeAtTick;
      const pose = sampled?.goldens[golden.id];
      slot.x = pose ? pose.x : golden.x;
      slot.y = pose ? pose.y : golden.y;
      slot.vx = pose ? pose.vx : golden.vx;
      slot.vy = pose ? pose.vy : golden.vy;
    }
    return dest;
  }

  private async onEnd(payload: unknown): Promise<void> {
    if (this.left) return;
    const end = parseMatchEnd(payload);
    if (!end) return;
    this.clearMatchEndWait();
    if (!this.session.state.result) {
      // Safety if the final SNAPSHOT was dropped (same tick as the last apply).
      this.onSync(() => {
        this.session.applyServerState(
          {
            ...this.session.state,
            result: end.result,
            clock: { ...this.session.state.clock, phase: 'ENDED' },
          },
          [],
        );
      });
    }
    this.releaseMatchSocket();
    this.noteMatchOver(end.ladder ?? 'skipped');
  }

  private onFault(payload: unknown): void {
    if (this.left) return;
    const error = parseError(payload);
    this.fault = error?.message ?? 'The match server closed the seat.';
    this.onNote();
    this.releaseMatchSocket();
    if (error?.code === 'ROOM_FULL' || error?.code === 'BAD_JOIN') this.onDrop();
  }

  /**
   * Overlay copy after SNAPSHOT result / MATCH_END. Only claim ladder when the server says so.
   * A visitor match never reaches the ladder, so it says that instead.
   */
  private noteMatchOver(ladder: 'saved' | 'skipped' | 'failed' | 'pending'): void {
    clearResumeSeat();
    const unranked = unrankedNote(this.room, this.camp);
    if (ladder === 'pending') {
      if (!this.reported) this.session.resultNote = unranked ?? 'Saving the score…';
      this.onNote();
      return;
    }
    this.reported = true;
    if (unranked) this.session.resultNote = unranked;
    else if (ladder === 'saved') this.session.resultNote = 'Score saved on the ladder.';
    else if (ladder === 'failed') this.session.resultNote = 'Could not save the score.';
    else this.session.resultNote = 'Match over.';
    this.onNote();
  }
}
